import React from 'react';
import {
  Box,
  Typography,
  Popover,
  Paper,
  TextField,
  InputAdornment,
} from '@mui/material';
import { KeyboardArrowDown, Search, Check } from '@mui/icons-material';
import { getPartners, setPartner } from '@/features/profile/profileQueries.ts';
import { useAppSelector } from '@/app';

// Team interface for the dropdown
export interface Team {
  id: string;
  name: string;
  role?: string;
}

export const TeamSelector: React.FC = ({}) => {
  const { partners: teams, loading } = getPartners();
  const { handlePartnerChange } = setPartner();
  const { user } = useAppSelector(state => state.auth);

  const [selectedTeam, setSelectedTeam] = React.useState<string | undefined>(
    user?.partner_id
  );
  const [teamDropdownAnchor, setTeamDropdownAnchor] =
    React.useState<HTMLElement | null>(null);
  const [searchQuery, setSearchQuery] = React.useState<string>('');
  const [focusedIndex, setFocusedIndex] = React.useState<number>(-1);
  const listRef = React.useRef<HTMLDivElement>(null);

  const handleTeamDropdownOpen = (event: React.MouseEvent<HTMLElement>) => {
    setTeamDropdownAnchor(event.currentTarget);
    setFocusedIndex(-1);
  };

  const handleTeamDropdownClose = () => {
    setTeamDropdownAnchor(null);
    setSearchQuery('');
    setFocusedIndex(-1);
  };

  const handleTeamChange = (teamId: string) => {
    handlePartnerChange(teamId);
    setSelectedTeam(teamId);
    setTeamDropdownAnchor(null);
    setSearchQuery('');
    setFocusedIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!filteredTeams?.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedIndex(prev => {
        const next = prev < filteredTeams.length - 1 ? prev + 1 : 0;
        scrollToItem(next);
        return next;
      });
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedIndex(prev => {
        const next = prev > 0 ? prev - 1 : filteredTeams.length - 1;
        scrollToItem(next);
        return next;
      });
    } else if (e.key === 'Enter' && focusedIndex >= 0) {
      e.preventDefault();
      handleTeamChange(filteredTeams[focusedIndex].id);
    }
  };

  const scrollToItem = (index: number) => {
    if (!listRef.current) return;
    const item = listRef.current.children[index] as HTMLElement;
    item?.scrollIntoView({ block: 'nearest' });
  };

  if (loading) {
    return null;
  }

  // Filter teams based on search query
  const filteredTeams = teams?.filter(
    team =>
      team.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      team.role?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const currentTeam =
    teams?.find(team => team.id === selectedTeam) || teams?.[0];

  if (!currentTeam) {
    return null;
  }

  return (
    <Box>
      {/* Team Selection Button */}
      <Box
        onClick={handleTeamDropdownOpen}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: { xs: 1, sm: 1.25 },
          cursor: 'pointer',
          borderRadius: 1,
          transition: 'all 0.15s ease-in-out',
          '&:hover': {
            backgroundColor: 'action.hover',
            transform: 'translateX(1px)',
          },
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              fontSize: { xs: '0.8125rem', sm: '0.875rem' },
              fontWeight: 500,
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              color: 'text.primary',
            }}
          >
            {currentTeam.name}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontSize: { xs: '0.6875rem', sm: '0.75rem' },
              lineHeight: 1,
              color: 'text.secondary',
              opacity: 0.8,
            }}
          >
            {currentTeam.role}
          </Typography>
        </Box>
        <KeyboardArrowDown
          sx={{
            color: 'text.secondary',
            fontSize: { xs: '0.875rem', sm: '1rem' },
            flexShrink: 0,
            opacity: 0.7,
            transition: 'opacity 0.15s',
          }}
        />
      </Box>

      {/* Team Selection Popover */}
      <Popover
        open={Boolean(teamDropdownAnchor)}
        anchorEl={teamDropdownAnchor}
        onClose={handleTeamDropdownClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            minWidth: 180,
            maxWidth: 280,
            width: 'auto',
            maxHeight: 240,
            mt: 0.25,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            borderRadius: 1.5,
            border: '1px solid',
            borderColor: 'divider',
          },
        }}
      >
        <Paper
          sx={{
            p: 0,
            borderRadius: 1.5,
            overflow: 'hidden',
          }}
        >
          {/* Search Bar */}
          <Box sx={{ p: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search teams..."
              value={searchQuery}
              onChange={e => {
                setSearchQuery(e.target.value);
                setFocusedIndex(-1);
              }}
              onKeyDown={handleKeyDown}
              autoFocus
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search
                      sx={{ fontSize: '0.875rem', color: 'text.secondary' }}
                    />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: '0.8125rem',
                  height: '32px',
                  '& fieldset': {
                    borderColor: 'divider',
                  },
                  '&:hover fieldset': {
                    borderColor: 'primary.main',
                  },
                },
                '& .MuiOutlinedInput-input': {
                  py: 0.75,
                },
              }}
            />
          </Box>

          {/* Team List */}
          <Box
            ref={listRef}
            sx={{
              maxHeight: 180,
              overflowY: 'auto',
              overflowX: 'hidden',
              '&::-webkit-scrollbar': {
                width: '3px',
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'divider',
                borderRadius: '1.5px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                backgroundColor: 'primary.main',
              },
            }}
          >
            {filteredTeams?.length ? (
              filteredTeams?.map((team, index) => (
                <Box
                  key={team.id}
                  onClick={() => handleTeamChange(team.id)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 1.25,
                    py: 0.875,
                    cursor: 'pointer',
                    transition: 'all 0.15s ease-in-out',
                    whiteSpace: 'nowrap',
                    borderBottom:
                      index < filteredTeams?.length - 1 ? '1px solid' : 'none',
                    borderColor: 'divider',
                    '&:hover': {
                      backgroundColor:
                        selectedTeam === team.id
                          ? 'primary.dark'
                          : 'action.hover',
                      transform: 'translateX(2px)',
                    },
                    ...(selectedTeam === team.id && {
                      backgroundColor: 'primary.main',
                      color: 'primary.contrastText',
                      fontWeight: 500,
                    }),
                    ...(focusedIndex === index &&
                      selectedTeam !== team.id && {
                        backgroundColor: 'action.focus',
                      }),
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      mr: selectedTeam === team.id ? 0.75 : 0,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: '0.8125rem',
                        fontWeight: selectedTeam === team.id ? 500 : 400,
                        lineHeight: 1.2,
                        whiteSpace: 'nowrap',
                        color: 'inherit',
                      }}
                    >
                      {team.name}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: '0.6875rem',
                        lineHeight: 1,
                        opacity: selectedTeam === team.id ? 0.85 : 0.65,
                        whiteSpace: 'nowrap',
                        color: 'inherit',
                      }}
                    >
                      {team.role}
                    </Typography>
                  </Box>
                  {selectedTeam === team.id && (
                    <Check
                      sx={{
                        fontSize: '0.875rem',
                        flexShrink: 0,
                        opacity: 0.9,
                      }}
                    />
                  )}
                </Box>
              ))
            ) : (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: '0.8125rem' }}
                >
                  No teams found
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>
      </Popover>
    </Box>
  );
};
